import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useMemo } from "react";
import { PainelPedido } from "@/components/pedido/PainelPedido";
import { useResolvePedidoId } from "@/hooks/useResolvePedidoId";
import { Skeleton } from "@/components/ui/skeleton";
import { useQueryClient } from "@tanstack/react-query";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { toast } from "sonner";
import { PendingSavesProvider } from "@/contexts/PendingSavesContext";

type Origin = "kanban" | "planilha" | "visao360" | null;
type Ctx = { 
  open: (idOrCode: string, origin?: Origin) => void; 
  close: () => void; 
  isOpen: boolean;
  isReady: boolean;
  _fromProvider: boolean;
};

// ✅ Event Bus global para fallback quando contexto não está disponível
const PAINEL_PEDIDO_EVENT = 'painel-pedido:open';

const defaultContextValue: Ctx = {
  open: (id, origin) => {
    console.warn('⚠️ [PainelPedidoContext] Usando fallback via Event Bus:', { id, origin });
    // Disparar evento global para que o Provider real capture
    window.dispatchEvent(new CustomEvent(PAINEL_PEDIDO_EVENT, { 
      detail: { id, origin } 
    }));
  },
  close: () => {
    console.warn('⚠️ [PainelPedidoContext] Close chamado sem Provider');
  },
  isOpen: false,
  isReady: false,
  _fromProvider: false,
};

export const PainelPedidoContext = createContext<Ctx>(defaultContextValue);

function PainelPedidoWrapper({ idOrCode, origin, onClose }: { idOrCode: string; origin: Origin; onClose: () => void }) {
  const { pedidoId, itemSeq, loading, error } = useResolvePedidoId(idOrCode);

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
    );
  }

  if (error || !pedidoId) {
    return (
      <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center">
        <div className="bg-card p-6 rounded-lg shadow-lg max-w-md">
          <h3 className="font-semibold text-destructive mb-2">Não foi possível abrir o pedido</h3>
          <p className="text-sm text-muted-foreground mb-4">{error || "Pedido não encontrado"}</p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            Fechar
          </button>
        </div>
      </div>
    );
  }

  return (
    <PainelPedido 
      open 
      onOpenChange={(v) => !v && onClose()} 
      pedidoId={pedidoId} 
      origin={origin}
      focusItemSeq={itemSeq ?? undefined}
      highPriority={origin === "visao360"}
    />
  );
}

export function PainelPedidoProvider({ children }: { children: ReactNode }) {
  const [openedIdOrCode, setOpenedIdOrCode] = useState<string | null>(null);
  const [origin, setOrigin] = useState<Origin>(null);
  const [isReady] = useState(true); // ✅ Provider está pronto desde o início

  // ✅ Log de montagem do Provider
  useEffect(() => {
    console.log('✅ [PainelPedidoProvider] Provider montado e pronto:', { isReady });
  }, [isReady]);

  // ✅ Escutar eventos globais de fallback (quando usam contexto default)
  useEffect(() => {
    const handleGlobalOpen = (e: CustomEvent<{ id: string; origin: Origin }>) => {
      console.log('📡 [PainelPedidoProvider] Evento global recebido:', e.detail);
      if (e.detail?.id) {
        setOpenedIdOrCode(e.detail.id);
        setOrigin(e.detail.origin ?? null);
      }
    };
    
    window.addEventListener(PAINEL_PEDIDO_EVENT as any, handleGlobalOpen);
    return () => window.removeEventListener(PAINEL_PEDIDO_EVENT as any, handleGlobalOpen);
  }, []);

  const open = useCallback((idOrCode: string, o?: Origin) => {
    try {
      // ✅ VALIDAÇÃO: Verificar se provider está pronto
      if (!isReady) {
        console.error('❌ [PainelPedidoContext] Provider não está pronto!');
        toast.error('Sistema ainda está carregando. Aguarde...');
        return;
      }
      
      // ✅ VALIDAÇÃO: Verificar se idOrCode existe
      if (!idOrCode) {
        console.error('❌ [PainelPedidoContext] idOrCode ausente!');
        toast.error('ID do pedido não encontrado');
        return;
      }
      
      console.log('✅ [PainelPedidoContext] Abrindo painel:', { idOrCode, origin: o });
      setOpenedIdOrCode(idOrCode);
      setOrigin(o ?? null);
    } catch (error) {
      console.error('❌ [PainelPedidoContext] Erro ao abrir painel:', error);
      toast.error('Não abriu o pedido. Atualize a tela e tente de novo.');
    }
  }, [isReady]);
  
  const close = useCallback(() => {
    setOpenedIdOrCode(null); 
    setOrigin(null); 
  }, []);

  // ✅ Fechar painel com tecla ESC
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && openedIdOrCode) {
        console.log('⌨️ [PainelPedidoContext] ESC pressionado - fechando painel');
        close();
      }
    };
    
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [openedIdOrCode, close]);

  const value: Ctx = useMemo(() => ({ 
    open, 
    close, 
    isOpen: !!openedIdOrCode,
    isReady,
    _fromProvider: true
  }), [open, close, openedIdOrCode, isReady]);

  return (
    <PainelPedidoContext.Provider value={value}>
      <PendingSavesProvider>
        {children}
        {openedIdOrCode && (
          <ErrorBoundary
            fallback={(error) => (
              <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center">
                <div className="bg-card p-6 rounded-lg shadow-lg max-w-md">
                  <h3 className="font-semibold text-destructive mb-2">Erro ao abrir painel</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Ocorreu um erro inesperado ao tentar abrir o Painel do Pedido: {error.message}
                  </p>
                  <button
                    onClick={close}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
                  >
                    Fechar
                  </button>
                </div>
              </div>
            )}
          >
            <PainelPedidoWrapper idOrCode={openedIdOrCode} origin={origin} onClose={close} />
          </ErrorBoundary>
        )}
      </PendingSavesProvider>
    </PainelPedidoContext.Provider>
  );
}

export function usePainelPedido() {
  const ctx = useContext(PainelPedidoContext);
  
  // Warning apenas em dev, sem bloquear funcionalidade
  if (!ctx._fromProvider) {
    console.warn('⚠️ [usePainelPedido] Contexto default em uso - Provider pode não estar montado');
  }
  
  return ctx;
}
